import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LibraryHeader } from '../components/PageComponents/header/LibraryHeader';
import { ArtistCard } from '../components/PageComponents/cards/ArtistCard';
import { Artist } from '../components/PageComponents/artistList/artist';
import { fetchPaged } from '../components/PageComponents/pagination/fetchPaged';
import { usePagedUrl, totalPageCount } from '../components/PageComponents/pagination/usePagedUrl';
import { Pagination } from '../components/PageComponents/pagination/Pagination';
import FilterBar, { readFilters, type FilterSpec, type FilterState } from '../components/PageComponents/filters/FilterBar';
import { API_BASE } from '../config/api';
import { PlayerContext } from '../context/PlayerContext';
import '../components/PageComponents/cards/Cards.css';

const ARTIST_FILTER_SPEC: FilterSpec = {
    defaultSort: 'id',
    defaultOrder: 'asc',
    sorts: [
        { value: 'id', label: 'Default' },
        { value: 'name', label: 'Name' },
        { value: 'albums', label: 'Album count' },
        { value: 'recent', label: 'Recently added' },
    ],
    selects: [
        {
            name: 'genre',
            label: 'All genres',
            value: '',
            options: [], // populated from /api/genre below
            multi: true, // checkbox dropdown, repeated genre= params
        },
        {
            name: 'min_albums',
            label: 'Any album count',
            value: '',
            options: [
                { value: '2', label: '2+ albums' },
                { value: '3', label: '3+ albums' },
                { value: '5', label: '5+ albums' },
                { value: '10', label: '10+ albums' },
            ],
        },
    ],
};

export default function Artists(): React.JSX.Element {
    const navigate = useNavigate();
    const { tracks, refreshTracks, setQueue, currentSong, isPlaying, togglePlay } = useContext(PlayerContext);
    const [artists, setArtists] = useState<Artist[]>([]);
    const [loading, setLoading] = useState(true);
    // total=null means "not known yet" - the page renders unpaginated
    // until the first response reports the real count.
    const [total, setTotal] = useState<number | null>(null);
    const [storedPerPage, setStoredPerPage] = useState<number | undefined>(undefined);
    // Genre options for the multi-select come from the read-only /api/genre
    // endpoint (same source as albums.tsx). Without this the artist genre
    // filter renders an empty dropdown.
    const [genreOptions, setGenreOptions] = useState<{ value: string; label: string }[]>([]);
    const { page, perPage, setPage, setPerPage } = usePagedUrl({ storedPerPage });

    // Filter/sort state, mirrored into the URL query string (?q=&sort=&
    // order=&genre=&min_albums=). Changing any of it resets to page 1.
    const [filterState, setFilterState] = useState<FilterState>(() => readFilters(ARTIST_FILTER_SPEC));
    const filterParams = useMemo(() => {
        // fetchPaged takes Record<string,string>; repeated genre= params are
        // joined into one string the fetcher splits on |  (see below).
        return {
            q: filterState.q,
            sort: filterState.sort,
            order: filterState.order,
            genre: filterState.multi.genre ?? [],
            min_albums: filterState.extra.min_albums ?? '',
        };
    }, [filterState]);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/genre`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
            .then((rows) => {
                if (cancelled || !Array.isArray(rows)) return;
                setGenreOptions(
                    rows.map((g: any) => ({ value: String(g.name ?? g), label: String(g.name ?? g) })),
                );
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    // The server-stored page size (ui.pagination.per_page) seeds the
    // default; without it the client default of 50 applies. Read-only,
    // best-effort: a failure just leaves the default in place.
    useEffect(() => {
        let cancelled = false;
        fetch(`${API_BASE}/api/settings/ui`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : {}))
            .then((prefs: Record<string, string>) => {
                if (cancelled) return;
                const stored = Number.parseInt(prefs['ui.pagination.per_page'] ?? '', 10);
                if (!Number.isNaN(stored) && stored >= 1) setStoredPerPage(stored);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        refreshTracks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const envelope = await fetchPaged<any>('/api/artist', page, perPage, filterParams);
            if (cancelled) return;
            setArtists(envelope.items.map((a: any) => Artist.fromJson(a)));
            setTotal(envelope.total);
            // A stale URL (e.g. ?page=9 after the list shrank) snaps back
            // into range instead of showing a permanently empty page.
            const maxPage = totalPageCount(envelope.total, envelope.per_page);
            if (envelope.page > maxPage) {
                setPage(maxPage);
                return;
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, filterParams]);

    const spec = useMemo<FilterSpec>(
        () => ({
            ...ARTIST_FILTER_SPEC,
            selects: ARTIST_FILTER_SPEC.selects.map((s) => (s.name === 'genre' ? { ...s, options: genreOptions } : s)),
        }),
        [genreOptions],
    );

    const totalPages = totalPageCount(total ?? 0, perPage);

    return (
        <>
            <div className="pageHeader">
                <LibraryHeader eyebrow="Your library" title="Artists" />
            </div>
            <div className="pageContent">
                <FilterBar spec={spec} state={filterState} onChange={setFilterState} resultCount={total ?? undefined} />
                {loading ? (
                    <div className="trackListEmpty">Loading artists…</div>
                ) : artists.length === 0 ? (
                    <div className="trackListEmpty">No artists in library</div>
                ) : (
                    <div className="cardGrid">
                        {artists.map((artist) => {
                            const isThisArtistCurrent = currentSong?.getArtists().some((a) => a.id === artist.getId());
                            return (
                                <ArtistCard
                                    key={artist.getId()}
                                    artist={artist}
                                    onClick={(a) => navigate(`/artists/${a.getId()}`)}
                                    isPlaying={isThisArtistCurrent && isPlaying}
                                    onPlay={(a) => {
                                        if (isThisArtistCurrent) togglePlay();
                                        else setQueue(tracks.filter((t) => t.getArtists().some((ca) => ca.id === a.getId())), 0);
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
                {total !== null && totalPages > 1 && (
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        perPage={perPage}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                    />
                )}
            </div>
        </>
    );
}
